/**
 * Lead sharing helpers.
 *
 * Builds a professional, human-readable summary of a demo-registration /
 * inquiry lead and opens it in either WhatsApp (mobile → WhatsApp / WhatsApp
 * Business; desktop → WhatsApp Web contact picker) or ChatGPT with the
 * summary prefilled as a prompt.
 *
 * Kept UI-agnostic so it can be reused from LeadDetailModal, mobile FAB,
 * card menus, etc.
 */

/** Human-friendly labels for lead field keys (any missing key falls back to
 *  Title-cased key). Ordered — first-appearing key wins. */
const FIELD_LABELS = {
    name: "Student Name",
    student_name: "Student Name",
    parent_name: "Parent Name",
    phone: "Phone",
    email: "Email",
    country: "Country",
    state: "State",
    city: "City",
    area: "Area",
    address_area: "Area",
    pin_code: "PIN Code",
    permanent_address: "Address",
    grade: "Grade",
    board: "Board",
    subjects: "Subjects",
    school_name: "School",
    medium_of_schooling: "Medium of Schooling",
    mode: "Mode",
    preferred_mode: "Preferred Mode",
    curriculum_preference: "Curriculum",
    exam_goal: "Exam Goal",
    learning_level: "Learning Level",
    language_preference: "Language Preference",
    preferred_tutor_gender: "Preferred Tutor Gender",
    preferred_demo_date: "Preferred Demo Date",
    preferred_demo_time: "Preferred Demo Time",
    preferred_demo_tz: "Time Zone",
    preferred_timing: "Preferred Timing",
    preferred_days: "Preferred Days",
    preferred_time_window: "Preferred Time Window",
    class_duration_minutes: "Class Duration (min)",
    batch_type: "Batch Type",
    quoted_fee: "Quoted Fee",
    tutor_in_hand_fee: "Tutor In-Hand Fee",
    fee_type: "Fee Type",
    additional_requirements: "Additional Requirements",
    special_notes: "Special Notes",
    message: "Message",
    lead_source: "Source",
    created_at: "Submitted At",
};

/** Field render order — the summary is built in this order so the most
 *  important context (who + how to reach + what to teach) comes first. */
const FIELD_ORDER = [
    "name",
    "student_name",
    "parent_name",
    "phone",
    "email",
    "country",
    "state",
    "city",
    "area",
    "address_area",
    "pin_code",
    "permanent_address",
    "grade",
    "board",
    "subjects",
    "school_name",
    "medium_of_schooling",
    "mode",
    "preferred_mode",
    "curriculum_preference",
    "exam_goal",
    "learning_level",
    "language_preference",
    "preferred_tutor_gender",
    "preferred_demo_date",
    "preferred_demo_time",
    "preferred_demo_tz",
    "preferred_timing",
    "preferred_days",
    "preferred_time_window",
    "class_duration_minutes",
    "batch_type",
    "quoted_fee",
    "tutor_in_hand_fee",
    "fee_type",
    "additional_requirements",
    "special_notes",
    "message",
    "lead_source",
    "created_at",
];

/** Internal fields we never surface to the shared message. */
const HIDDEN_FIELDS = new Set([
    "id",
    "_id",
    "status",
    "assigned_to",
    "notes_log",
    "created_by",
    "updated_at",
    "pipeline_stage",
    "pipeline_sub_status",
    "assigned_tutor_id",
    "assigned_tutor_name",
    "assigned_at",
    "phone_country",
    "phone_country_missing",
    "last_wa_sent_at",
    "last_wa_template",
    "last_wa_language",
    "activity_log",
    "reply_generated_at",
    "reply_generated_by",
    "additional_students",
    "wants_all_major_subjects",
    "lead_type",
]);

function _formatValue(v) {
    if (v == null) return "";
    if (Array.isArray(v)) return v.filter(Boolean).join(", ");
    if (typeof v === "object") return "";
    return String(v).trim();
}

function _titleCase(key) {
    return key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Build a professional multi-line lead summary string.
 * Ready to be pasted into WhatsApp / ChatGPT — includes emoji header,
 * key:value lines, and a friendly closing.
 */
export function buildLeadShareText(item, kind = "registrations") {
    if (!item || typeof item !== "object") return "";

    const kindLabel =
        kind === "inquiries" ? "New Inquiry"
        : kind === "app-leads" ? "App Enquiry"
        : kind === "tutor-applications" ? "Tutor Application"
        : "New Demo Registration";

    const lines = [];
    lines.push(`*${kindLabel} — Anand Tutorials*`);
    lines.push("");

    const seen = new Set();
    const push = (key, value) => {
        const val = _formatValue(value);
        if (!val) return;
        const label = FIELD_LABELS[key] || _titleCase(key);
        lines.push(`• ${label}: ${val}`);
    };

    // Strict whitelist: only emit keys that appear in FIELD_ORDER above.
    // A trailing "future-proof" fallback loop was intentionally removed —
    // backend docs carry internal migration / telemetry columns (e.g.
    // `phone_country_inferred`, `pipeline_migrated_at`) that must never
    // appear in a message shared with parents / colleagues / ChatGPT.
    for (const k of FIELD_ORDER) {
        if (HIDDEN_FIELDS.has(k)) continue;
        if (seen.has(k)) continue;
        if (!(k in item)) continue;
        push(k, item[k]);
        seen.add(k);
    }

    lines.push("");
    lines.push("_Shared from Admin Dashboard._");
    return lines.join("\n");
}

/**
 * Build a ChatGPT-friendly prompt with the lead summary as context and a
 * request to draft a response / follow-up.
 */
export function buildChatGPTPrompt(item, kind = "registrations") {
    const summary = buildLeadShareText(item, kind);
    return [
        "You are a warm, professional tuition-coordinator at Anand Tutorials (India).",
        "A parent just submitted this demo-tuition request:",
        "",
        summary,
        "",
        "Draft:",
        "1. A short WhatsApp confirmation reply (2-3 lines, friendly, Hindi-English mix if the parent looks Indian, otherwise English).",
        "2. A follow-up talking point checklist for the coordinator's first call (5 bullets).",
        "",
        "Keep it concise, positive, and action-oriented.",
    ].join("\n");
}

/** Detect if we're on a small-screen device — used to pick between
 *  wa.me (universal) and whatsapp:// (native app deep-link). */
function _isMobile() {
    if (typeof navigator === "undefined") return false;
    return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

/**
 * Open WhatsApp (Business chooser on Android when it's installed as the
 * default WhatsApp handler; WhatsApp Web with contact picker on desktop)
 * with the lead summary preloaded as the message text.
 *
 * The share URL intentionally omits a phone number so the user picks the
 * recipient (colleague, tutor, admin group) themselves.
 */
export function openWhatsAppShare(text) {
    const encoded = encodeURIComponent(text);
    // wa.me works cross-platform. On Android with WA Business installed as
    // the default WhatsApp app, Chrome routes wa.me to WA Business via the
    // WhatsApp app intent handler. On desktop it opens WhatsApp Web.
    const url = `https://wa.me/?text=${encoded}`;
    window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Open ChatGPT (chatgpt.com) with the lead summary prefilled as the
 * initial prompt via the `?q=` search parameter — same behavior the
 * ChatGPT web app uses for shared links.
 */
export function openChatGPTShare(text) {
    const encoded = encodeURIComponent(text);
    const url = `https://chatgpt.com/?q=${encoded}`;
    window.open(url, "_blank", "noopener,noreferrer");
}

/** Copy the summary to the OS clipboard. Returns a promise that resolves
 *  true on success (falls back to a hidden textarea + document.execCommand
 *  on older browsers).
 */
export async function copyLeadText(text) {
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (_e) { /* fall through to legacy path */ }
    try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
    } catch (_e) {
        return false;
    }
}

// Re-export mobile detector for consumers that want to tweak UX
export const isMobileDevice = _isMobile;
